const DateUtil = {
  /**
   * 时间转换
   * eg: 2021-07-27 19:00:00 -> parseDateTime(time, '{y}-{m}-{d} {h}:{i}{s}')
   * cFormat可以根据自己需求格式写
   * eg: 2021/07/27 19:00 -> parseDateTime(time, '{y}/{m}/{d} {h}:{i}')
   * eg: 19:00 -> parseDateTime(time, '{h}:{i}')
   * 转换为周几: `周${parseDateTime(time, '{a}')}`
   * @param {(string|number|Date)} dateVal
   * @param {string} cFormat
   * @returns {string | null}
   */
  parseDateTime: (dateVal, cFormat) => {
    /* eslint-disable no-undef */
    if (arguments.length === 0 || !dateVal) {
      return null
    }
    const format = cFormat || '{y}-{m}-{d} {h}:{i}:{s}'
    const date = DateUtil.getDateObject(dateVal)
    const formatObj = {
      y: date.getFullYear(),
      m: date.getMonth() + 1,
      d: date.getDate(),
      h: date.getHours(),
      i: date.getMinutes(),
      s: date.getSeconds(),
      a: date.getDay()
    }
    const timeStr = format.replace(/{([ymdhisa])+}/g, (result, key) => {
      const value = formatObj[key]
      // Note: getDay() returns 0 on Sunday
      if (key === 'a') {
        return ['日', '一', '二', '三', '四', '五', '六'][value]
      }
      return value.toString().padStart(2, '0')
    })
    return timeStr
  },
  dateFormat: dateVal => {
    return DateUtil.parseDateTime(dateVal, '{y}/{m}/{d}')
  },
  dateTimeFormat: dateVal => {
    return DateUtil.parseDateTime(dateVal, '{y}/{m}/{d} {h}:{i}')
  },
  /**
   * 距当前时间多久之前
   * @param {(string|string|Date)} dateVal
   * @returns {string}
   */
  timeAgoStr: dateVal => {
    const date = DateUtil.getDateObject(dateVal)
    if (!date) return ''
    const time = new Date().getTime() - date.getTime()
    if (time < 0) {
      return ''
    } else if (time / 1000 < 60) {
      return '刚刚'
    } else if (time / 60000 < 60) {
      return Math.floor(time / 60000) + '分钟前'
    } else if (time / 3600000 < 24) {
      return Math.floor(time / 3600000) + '小时前'
    } else if (time / 86400000 < 31) {
      return Math.floor(time / 86400000) + '天前'
    } else if (time / 2592000000 < 12) {
      return Math.floor(time / 2592000000) + '月前'
    } else {
      return Math.floor(time / 31536000000) + '年前'
    }
  },
  formatWorkdayStr: dateVal => {
    /**
     * e.g 昨天 | 今天 | 明天 | 明天 | 周X
     * @param {(string|number|Date))} dateVal
     * @returns {string}
     */
    const currentTimestamp = new Date().getTime()
    const preTimestamp = currentTimestamp - 60 * 60 * 24 * 1 * 1000
    const tomorrowTimestamp = currentTimestamp + 60 * 60 * 24 * 1000
    const dayAfterTimestamp = currentTimestamp + 60 * 60 * 24 * 2 * 1000
    let ret = ''
    const cFormat = '{y}-{m}-{d}'
    if (DateUtil.parseDateTime(preTimestamp, cFormat) === DateUtil.parseDateTime(dateVal, cFormat)) {
      ret = '昨天'
    } else if (DateUtil.parseDateTime(currentTimestamp, cFormat) === DateUtil.parseDateTime(dateVal, cFormat)) {
      ret = '今天'
    } else if (DateUtil.parseDateTime(tomorrowTimestamp, cFormat) === DateUtil.parseDateTime(dateVal, cFormat)) {
      ret = '明天'
    } else if (DateUtil.parseDateTime(dayAfterTimestamp, cFormat) === DateUtil.parseDateTime(dateVal, cFormat)) {
      ret = '后天'
    } else {
      ret = '周' + DateUtil.parseDateTime(dateVal, '{a}')
    }
    return ret
  },
  /**
   * e.g 2021-08-07T22:56:20+0800 => Date Object
   * @param {string} dateString
   * @returns {Date | Null}
   */
  getDateObjectByString: dateString => {
    // 为了兼容ios的时间格式转化
    const convertedDateString = dateString
      .replace(/-/g, '/')
      .replace('T', ' ')
      .replace(/\+[0-9:]+/gi, '')
    return convertedDateString ? new Date(convertedDateString) : null
  },
  /**
   * e.g Date Object => 2021-08-07T22:56:20+0800
   * @param {string} dateString
   * @returns {String}
   */
  formatGMTDate: dateString => {
    const date = DateUtil.getDateObject(dateString)
    if (!date) return ''
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    const hour = date.getHours()
    const minute = date.getMinutes()
    const second = date.getSeconds()
    const t1 = [year, month, day].map(DateUtil.formatNumber).join('-')
    const t2 = [hour, minute, second].map(DateUtil.formatNumber).join(':')
    return `${t1}T${t2}+0800`
  },
  /**
   * e.g 2021-08-07T22:56:20+0800, timestamp, Date Object
   * @param {(string|number|Date)} dateVal
   * @returns {Date | Null}
   */
  getDateObject: dateVal => {
    let date = null
    if (typeof dateVal === 'string') {
      date = DateUtil.getDateObjectByString(dateVal)
    } else if (typeof dateVal === 'number' && isFinite(dateVal)) {
      date = new Date(dateVal)
    } else if (dateVal instanceof Date) {
      date = dateVal
    }
    return date
  },
  formatNumber: n => {
    const str = n.toString()
    return str[1] ? str : `0${str}`
  }
}

export { DateUtil }
